import { useMemo } from "react";
import { Navigate, Route, Routes, useParams } from "react-router";
import { allows, type Area } from "@shared/permissions";
import type { HubSite } from "@/lib/hub";
import { siteApi } from "@/lib/siteApi";
import { openSite } from "./current";
import { AdminFooterLinks, AdminNav } from "./admin/AdminNav";
import * as P from "./pages";

// A TASMA-kind website's dashboard in the hub (/sites/:siteId/*): the website's own dashboard screens, talking to its
// admin API with site tokens. Tabs and pages the person may not use aren't shown; the website refuses them anyway.

export function TasmaSite({ sites }: { sites: HubSite[] }) {
  const { siteId = "" } = useParams();
  const site = sites.find((s) => s.id === siteId);
  const api = useMemo(() => (site ? siteApi(site.id) : null), [site]);
  if (!site || !api) return <Navigate to="/" replace />;
  openSite(api, `/sites/${site.id}`, site.url);
  const can = (area: Area) => allows(site.owner, site.grant, area, "view");
  const guard = (area: Area, page: React.ReactNode) => (can(area) ? page : <Navigate to={`/sites/${site.id}`} replace />);

  return (
    <div className="min-h-svh bg-offwhite text-midnight lg:grid lg:grid-cols-[15rem_1fr]">
      <AdminNav siteName={site.name} can={can} />
      <main id="main" className="min-w-0 px-4 py-6 sm:px-6 lg:px-10 lg:py-10">
        <div className="mx-auto max-w-4xl">
          <Routes>
            <Route index element={<P.OverviewPage can={can} />} />
            <Route path="publish" element={guard("settings", <P.PublishPage />)} />
            <Route path="stats" element={guard("stats", <P.StatsPage canLeads={can("leads")} />)} />
            <Route path="leads" element={guard("leads", <P.LeadsPage />)} />
            <Route path="scores" element={guard("scores", <P.ScoresPage />)} />
            <Route path="blog" element={guard("content", <P.BlogPage />)} />
            <Route path="blog/:id" element={guard("content", <P.PostPage />)} />
            <Route path="projects" element={guard("content", <P.ProjectsPage />)} />
            <Route path="projects/:id" element={guard("content", <P.ProjectPage />)} />
            <Route path="services" element={guard("content", <P.ServicesPage />)} />
            <Route path="services/:id" element={guard("content", <P.ServicePage />)} />
            <Route path="faq" element={guard("content", <P.FaqPage />)} />
            <Route path="pages" element={guard("content", <P.TextPage />)} />
            <Route path="interface" element={guard("content", <P.InterfacePage />)} />
            <Route path="settings" element={guard("settings", <P.SettingsPage />)} />
            <Route path="media" element={guard("media", <P.MediaPage />)} />
            <Route path="history" element={guard("history", <P.HistoryPage />)} />
            <Route path="*" element={<Navigate to={`/sites/${site.id}`} replace />} />
          </Routes>
          <AdminFooterLinks />
        </div>
      </main>
    </div>
  );
}
